import { Activity, Contact, Donation, Event, Partner, Poster, RSVPEntry, Product, Order, Role, RoleWithPermissions, Permission, User, UserRole, AuditLog, Member } from './types';
import api from './axios';

export const members = {
  list: () => api.get<Member[]>('/members').then((res: { data: any; }) => res.data),
  updateMerchStatus: (id: string, merchDistributed: boolean) => api.patch<{ success: boolean }>(`/members/${id}/merch`, { merchDistributed }).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete<{ success: boolean }>(`/members/${id}`).then((res: { data: any; }) => res.data),
};

export const activities = {
  list: () => api.get<Activity[]>('/activities').then((res: { data: any; }) => res.data),
  metrics: () => api.get<{ total: number; upcoming: number; past: number; }>(`/activities/metrics`).then((res: { data: any; }) => res.data),
  create: (data: FormData) => api.post<Activity>('/activities', data).then((res: { data: any; }) => res.data),
  update: (id: string, data: FormData) => api.put<Activity>(`/activities/${id}`, data).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete(`/activities/${id}`).then((res: { data: any; }) => res.data),
};

export const donations = {
  list: () => api.get<Donation[]>('/donations').then((res: { data: any; }) => res.data),
  metrics: () => api.get<{ total: number; pending: number; approved: number; rejected: number; }>(`/donations/metrics`).then((res: { data: any; }) => res.data),
  create: (data: FormData) => api.post<Donation>('/donations', data).then((res: { data: any; }) => res.data),
  update: (id: string, data: FormData) => api.put<Donation>(`/donations/${id}`, data).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete(`/donations/${id}`).then((res: { data: any; }) => res.data),
};

export const events = {
  list: () => api.get<Event[]>('/events').then((res: { data: any; }) => res.data),
  create: (data: FormData) => api.post<Event>('/events', data).then((res: { data: any; }) => res.data),
  update: (id: string, data: FormData) => api.put<Event>(`/events/${id}`, data).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete(`/events/${id}`).then((res: { data: any; }) => res.data),
  getRSVPs: (eventId: string) => api.get<RSVPEntry[]>(`/events/${eventId}/rsvp`).then((res: { data: any; }) => res.data),
  submitRsvp: (eventId: string, data: FormData) =>
    api.post<RSVPEntry>(`/events/${eventId}/rsvp`, data).then((res: { data: any; }) => res.data),
  updateRsvp: (eventId: string, rsvpId: string, data: FormData) =>
    api.put<RSVPEntry>(`/events/${eventId}/rsvp/${rsvpId}`, data).then((res: { data: any; }) => res.data),
  deleteRsvp: (eventId: string, rsvpId: string) =>
    api.delete(`/events/${eventId}/rsvp/${rsvpId}`).then((res: { data: any; }) => res.data),
};

export const partners = {
  list: () => api.get<Partner[]>('/partners').then((res: { data: any; }) => res.data),
  create: (data: FormData) => api.post<Partner>('/partners', data).then((res: { data: any; }) => res.data),
  update: (id: string, data: FormData) => api.put<Partner>(`/partners/${id}`, data).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete(`/partners/${id}`).then((res: { data: any; }) => res.data),
};

export const contacts = {
  list: () => api.get<Contact[]>('/contacts').then((res: { data: any; }) => res.data),
  create: (data: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) =>
    api.post<Contact>('/contacts', data).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete(`/contacts/${id}`).then((res: { data: any; }) => res.data),
};

export const posters = {
  list: () => api.get<Poster[]>('/posters').then((res: { data: any; }) => res.data),
  create: (data: FormData) => api.post<Poster>('/posters', data).then((res: { data: any; }) => res.data),
  update: (id: string, data: FormData) => api.put<Poster>(`/posters/${id}`, data).then((res: { data: any; }) => res.data),
  toggle: (id: string) => api.patch<Poster>(`/posters/${id}/toggle`).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete(`/posters/${id}`).then((res: { data: any; }) => res.data),
};

export const products = {
  list: (params?: any) => api.get<{ products: Product[], total: number }>('/admin/products', { params }).then((res: { data: any; }) => res.data.products),
  get: (id: string) => api.get<Product>(`/admin/products/${id}`).then((res: { data: any; }) => res.data),
  create: (data: any) => api.post<Product>('/admin/products', data).then((res: { data: any; }) => res.data),
  update: (id: string, data: any) => api.put<Product>(`/admin/products/${id}`, data).then((res: { data: any; }) => res.data),
  delete: (id: string) => api.delete(`/admin/products/${id}`).then((res: { data: any; }) => res.data),
  /** Restock (+N) or correct (-N) relative to the live stock, atomically */
  adjustStock: (id: string, change: number, note?: string) =>
    api.post<{ stockQuantity: number }>(`/admin/products/${id}/stock`, { change, note }).then((res: { data: any; }) => res.data),
  getInventoryLogs: (id: string) => api.get<any[]>(`/admin/products/${id}/inventory-logs`).then((res: { data: any; }) => res.data),
};

export const orders = {
  list: (params?: any) => api.get<{ orders: Order[], total: number }>('/admin/orders', { params }).then((res: { data: any; }) => res.data.orders),
  get: (id: string) => api.get<Order>(`/admin/orders/${id}`).then((res: { data: any; }) => res.data),
  updateStatus: (id: string, status: string, extra?: { trackingNumber?: string; refundConfirmed?: boolean }) =>
    api.put<{ order: Order }>(`/admin/orders/${id}/status`, { status, ...extra }).then((res: { data: any; }) => res.data),
};

export const media = {
  uploadImage: (data: FormData) => api.post<{ url: string; filename: string }>('/media/upload/image', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res: { data: any; }) => res.data),
};

// RBAC Endpoints
export const roles = {
  list: () => api.get<Role[]>('/roles').then((res) => res.data),
  get: (id: string) => api.get<RoleWithPermissions>(`/roles/${id}`).then((res) => res.data),
  create: (data: { name: string; description: string }) => api.post<Role>('/roles', data).then((res) => res.data),
  update: (id: string, data: { name: string; description: string }) => api.put<Role>(`/roles/${id}`, data).then((res) => res.data),
  delete: (id: string) => api.delete(`/roles/${id}`).then((res) => res.data),
  updatePermissions: (id: string, permissionIds: string[]) => api.put(`/roles/${id}/permissions`, { permissionIds }).then((res) => res.data),
};

export const permissions = {
  list: () => api.get<{ permissions: Permission[]; grouped: Record<string, Permission[]> }>('/permissions').then((res) => res.data),
};

export const users = {
  list: (params?: { query?: string; offset?: number; limit?: number }) =>
    api.get<{ users: User[]; totalCount: number }>('/users', { params }).then((res) => res.data),
  get: (id: string) => api.get<User>(`/users/${id}`).then((res) => res.data),
  getRoles: (userId: string) => api.get<UserRole[]>(`/users/${userId}/roles`).then((res) => res.data),
  assignRole: (userId: string, roleId: string) => api.post(`/users/${userId}/roles`, { roleId }).then((res) => res.data),
  removeRole: (userId: string, roleId: string) => api.delete(`/users/${userId}/roles/${roleId}`).then((res) => res.data),
};

export const auditLogs = {
  list: (params?: { userId?: string; action?: string; resourceType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) =>
    api.get<{ logs: AuditLog[]; limit: number; offset: number }>('/audit-logs', { params }).then((res) => res.data),
};